import {
  OverviewDatum,
  OverviewLineData,
  SmplPntSeriesOfOverviewDatums,
} from '@resistapp/client/data-utils/plot-data/build-overview-line-data';
import { useStateWithRef } from '@resistapp/client/hooks/use-state-with-ref';
import { ApiError } from '@resistapp/client/utils/error';
import {
  OverviewChartConfiguration,
  getActiveAbundanceMode,
  getOverviewConfiguration,
} from '@resistapp/client/utils/overview-chart-configurations';
import type { ReferenceByMAEC } from '@resistapp/common/api-types';
import { type L2Target } from '@resistapp/common/assays';
import { SampleTypeGroup, SmplPntGroup } from '@resistapp/common/comparable-sample-groups';
import { KeyInsights } from '@resistapp/common/key-insights/types';
import type { PossibleZoomableLevel } from '@resistapp/common/types';
import {
  AbundanceMode,
  AdminArea,
  AdminLevelKey,
  FullSample,
  MetricMode,
  ProcessSelection,
  ProcessMode,
  getOriginalSamplingPointId,
} from '@resistapp/common/types';
import { Dictionary, keys } from 'lodash';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAssayContext } from '../assay-context';
import { useSampleDataContext } from '../sample-data-context';
import { buildKeyInsightsBySite } from './key-insights-by-site';
import { useOverviewData } from './hooks/use-overview-data';
import { useOverviewQueryParams } from './hooks/use-overview-query-params';
import { OverviewShownLevel, getOverviewLevelKey } from './overview-levels';
import { useSelectedSiteDatum } from './hooks/use-single-site-selected';
import { useSupportedSamples } from './hooks/use-supported-samples';
import { getAnalysedGroupsAndAll, getEffectiveSiteProcessMode } from './overview-context-utils';

export interface OverviewContextData {
  hasPreparedData: boolean;
  loading: boolean;
  error: Error | ApiError | null;

  // Each value in trendDataByLevel is an array of time serieS, one time series for each siteOrArea in the project (or for individual sites)
  // The special key 'null' has the trend data for individual sites, key '2' has country level data for admin areas, and so on.
  // Accessing a datum: trendDataByLevel[level][iSiteOrArea][time]
  // Literal structure: {lvl: [[env1_time1, env1_time2, ...], [env2_time1, env2_time3, ...], ...] }
  trendDataByLevel: Dictionary<OverviewLineData> | undefined;

  // Each value in mapDataByLevel is an array of siteOrArea datums for a selected timepoint or latest available timepoint for each siteOrArea.
  // The special key 'null' has the trend data for individual sites, key '2' has country level data for admin areas, and so on.
  // Accessing a datum: mapDataByLevel[level][iSiteOrArea]
  // Literal structure: {lvl: [latest_datum_for_env_1, latest_datum_for_env_2...] }
  mapDataByLevel: Dictionary<SmplPntSeriesOfOverviewDatums> | undefined;
  processGroupMapData: SmplPntSeriesOfOverviewDatums | undefined;

  // trendData: an array of siteOrArea time series, shortcut for trendDataByLevel[adminLevel]
  // Accessing a datum: trendData[iSiteOrArea][time]
  // Literal structure: [[env1_time1, env1_time2, ...], [env2_time1, env2_time3, ...], ...]
  trendData: OverviewLineData | undefined;

  // mapData: selected from mapDataByLevel with shownAdminLevel and filtered by selectedCountry
  // mapData has one data point for each siteOrArea picked 'vertically' from each siteTimeSeriesArrray in the outer trendData array
  // (eg. based on selected time, or latest timepoint for each siteOrArea)
  // Accessing a datum: mapData[iSiteOrArea]
  // Literal structure: [latest_datum_for_env_1, latest_datum_for_env_2...]
  mapData: SmplPntSeriesOfOverviewDatums | undefined;

  selectedAntibiotic: L2Target | undefined;
  selectedSiteDatum: OverviewDatum | undefined; // A single site selected for site view
  selectedMonth: Date | null;
  selectedSampleTypeGroup: SampleTypeGroup;
  hoveredAreaOrSiteSmplPntId: number | undefined;
  selectedOrHoveredAreaOrSiteSmplPntId: number | undefined; // Sampling point id (either area or site) that is currently hovered over, or selected in site view
  setHoveredAreaOrSiteSmplPntIdStable: React.Dispatch<React.SetStateAction<number | undefined>>;
  selectedCountry: string | undefined;
  levelsWithZoomableAreas: number[] | undefined;
  // NOTE => Used only in month-selector
  availableMonths: string[];
  setMonthStable: (date: Date | null, endDate: Date | null) => void;
  defaultProcessMode: ProcessMode; // Defaul process mode for the metric and selected sampling point type(s)
  effectiveSiteProcessMode: ProcessMode; // Site view process mode that's supported by the selected site
  metricMode: MetricMode;
  activeOverviewConfiguration: OverviewChartConfiguration;
  availableSmplPntGroups: SmplPntGroup[] | undefined;
  activeAbundanceMode: AbundanceMode;
  isOneHealthProject: boolean | undefined;
  shownAdminLevel: OverviewShownLevel;
  processGroupLevelEnabled: boolean;
  selectedProcessId: number | undefined;
  selectedStepPointId: number | undefined;
  selectedFlowId: number | undefined;
  comparedStepPointId: number | undefined;
  setProcessSelectionStable: React.Dispatch<React.SetStateAction<ProcessSelection>>;
  previousAdminAreasLifo: AdminArea[];
  zoomedMapData: OverviewDatum | undefined;
  metricGeneCnt: number | undefined;
  supportedSamples: FullSample[] | undefined;
  zoomedAdminAreaRef: React.MutableRefObject<AdminArea | null>;
  notAvailableReason: string | null; // TODO this is used at map container level, not only mapboxmap
  analyzedAntibioticGroups: Array<L2Target | 'All antibiotics'> | undefined;
  setZoomedAdminAreaStable: React.Dispatch<React.SetStateAction<AdminArea | null>>;
  setPreviousAdminAreasLifoStable: React.Dispatch<React.SetStateAction<AdminArea[]>>;
  setSelectedCountryStable: (country: string | undefined) => void;
  setShownAdminLevelStable: (level: OverviewShownLevel) => void;
  referenceLineData: ReferenceByMAEC | undefined;
  keyInsightsBySite: Dictionary<KeyInsights | null> | undefined;
}

interface ProviderProps {
  children: React.ReactNode;
  metricMode: MetricMode;
}

const OverviewContext = createContext<OverviewContextData | undefined>(undefined);

export function OverviewContextProvider({ children, metricMode }: ProviderProps) {
  // 0. LOAD INPUT DATA
  const { assaysLoaded, getGroup } = useAssayContext();
  const { queryFilters, loading, error, data } = useSampleDataContext();
  const referenceLineData = data?.overviewReferencesByMAEC;

  // INTERNAL STATE - Admin level and site selection
  const [zoomedAdminArea, setZoomedAdminAreaStable, zoomedAdminAreaRef] = useStateWithRef<AdminArea | null>(null);
  const [shownAdminLevel, setShownAdminLevelStable, shownAdminLevelRef] = useStateWithRef<OverviewShownLevel>(null);
  const [previousAdminAreasLifo, setPreviousAdminAreasLifoStable] = useState<AdminArea[]>([]);
  const [processSelection, setProcessSelectionStable] = useState<ProcessSelection>({});

  const [trendData, setTrendData] = useState<OverviewDatum[][] | undefined>();
  const [mapData, setMapData] = useState<OverviewDatum[] | undefined>();
  const [keyInsightsBySite, setKeyInsightsBySite] = useState<Dictionary<KeyInsights | null> | undefined>(undefined);
  const processSelectionFallbackKeysRef = useRef<Set<string>>(new Set());

  // 1. ENFORCE SUPPORTED QUERY STATES AND TRANSITIONS (availableSmplPntGroups)
  const {
    queryParamsInitialised,
    availableSmplPntGroups,
    selectedAntibiotic,
    activeSmplPntGroup,
    setSelectedCountryStable,
    countryParam,
  } = useOverviewQueryParams({
    metricMode,
  });

  // 2. KEEP ONLY SUPPORTED SAMPLES AND ADMIN LEVELS THAT ARE PRESENT IN ALL OF THEM
  const { supportedSamples, isOneHealthProject, metricGeneCnt, selectedCountry } = useSupportedSamples({
    availableSmplPntGroups,
    metricMode,
    selectedAntibiotic,
    countryParam,
  });

  // 3. PREPARE OVERVIEW DATA
  const {
    trendDataByLevel,
    mapDataByLevel,
    processGroupMapData,
    availableMonths,
    notAvailableReason,
    availableNormalisationModes,
    defaultProcessMode,
    processGroupLevelEnabled,
  } = useOverviewData({
    supportedSamples,
    queryParamsInitialised,
    activeSmplPntGroup,
    metricMode,
  });

  // Helpers
  // TODO WE WANNA SHOW PER LITTER AND RISK AS WELL !?!
  const activeAbundanceMode = getActiveAbundanceMode(availableNormalisationModes);
  const activeOverviewConfiguration = getOverviewConfiguration(metricMode, activeAbundanceMode);
  const zoomedMapData = useMemo(() => {
    if (!zoomedAdminArea || !mapDataByLevel) {
      return undefined;
    }
    return mapDataByLevel[zoomedAdminArea.level].find(area => area.samplingPoint.name === zoomedAdminArea.name);
  }, [zoomedAdminArea, mapDataByLevel]);

  // SITE SELECTION STATE
  const [hoveredAreaOrSiteSmplPntId, setHoveredAreaOrSiteSmplPntIdStable] = useState<number | undefined>(undefined);
  const selectedSiteDatum = useSelectedSiteDatum({
    supportedSamples,
    mapData,
  });
  const selectedFlowSiteDatum = useMemo(() => {
    if (metricMode !== MetricMode.REDUCTION || shownAdminLevel !== null || !processSelection.selectedFlowId || !mapData?.length) {
      return undefined;
    }

    const selectedFlow = data?.processes
      ?.flatMap(process => process.flows ?? [])
      .find(flow => flow.id === processSelection.selectedFlowId);
    if (!selectedFlow) {
      return undefined;
    }

    const fromDatum = mapData.find(datum => doesDatumContainSamplingPoint(datum, selectedFlow.fromPointId));
    const toDatum = mapData.find(datum => doesDatumContainSamplingPoint(datum, selectedFlow.toPointId));
    if (!fromDatum || !toDatum) {
      return undefined;
    }

    return {
      ...fromDatum,
      samplingPointAfter: toDatum.samplingPoint,
      beforeAbundances: getPrimaryStepAbundances(fromDatum),
      afterAbundances: getPrimaryStepAbundances(toDatum),
    };
  }, [data?.processes, mapData, metricMode, processSelection.selectedFlowId, shownAdminLevel]);
  const selectedSiteDatumForContext = selectedFlowSiteDatum ?? selectedSiteDatum;

  useEffect(() => {
    if (!processGroupLevelEnabled || !data?.processes?.length) {
      setProcessSelectionStable({});
      return;
    }

    const hasSelectedProcess =
      !!processSelection.selectedProcessId &&
      data.processes.some(process => process.id === processSelection.selectedProcessId);
    if (hasSelectedProcess) {
      return;
    }

    const hasAnyProcessSelection =
      !!processSelection.selectedProcessId ||
      !!processSelection.selectedStepPointId ||
      !!processSelection.selectedFlowId ||
      !!processSelection.comparedStepPointId;
    if (!hasAnyProcessSelection) {
      return;
    }

    logProcessSelectionFallback({
      fallbackKeysRef: processSelectionFallbackKeysRef,
      reason: 'missing-process',
      selectedProcessId: processSelection.selectedProcessId,
      selectedFlowId: processSelection.selectedFlowId,
      selectedStepPointId: processSelection.selectedStepPointId,
    });

    setProcessSelectionStable({});
  }, [
    processGroupLevelEnabled,
    data?.processes,
    processSelection.comparedStepPointId,
    processSelection.selectedFlowId,
    processSelection.selectedProcessId,
    processSelection.selectedStepPointId,
    setProcessSelectionStable,
  ]);

  useEffect(() => {
    if (metricMode !== MetricMode.REDUCTION || shownAdminLevel !== null || !processSelection.selectedFlowId || selectedFlowSiteDatum) {
      return;
    }

    logProcessSelectionFallback({
      fallbackKeysRef: processSelectionFallbackKeysRef,
      reason: 'missing-transition-selection',
      selectedProcessId: processSelection.selectedProcessId,
      selectedFlowId: processSelection.selectedFlowId,
      selectedStepPointId: processSelection.selectedStepPointId,
      mapDataCount: mapData?.length ?? 0,
      processCount: data?.processes?.length ?? 0,
    });
  }, [
    data?.processes?.length,
    mapData?.length,
    metricMode,
    processSelection.selectedFlowId,
    processSelection.selectedProcessId,
    processSelection.selectedStepPointId,
    selectedFlowSiteDatum,
    shownAdminLevel,
  ]);

  // 4.1 SELECT TREND DATA BASED ON SHOWN LEVEL
  const selectedSiteSmplPntId = selectedSiteDatumForContext?.samplingPoint.id;
  useEffect(() => {
    if (trendDataByLevel) {
      const adminLevelKey = getOverviewLevelKey(shownAdminLevel);
      const newTrendData = trendDataByLevel[adminLevelKey] ?? [];
      // When one specific site is selected (transient aggregated sampling points have negative ids)
      if (shownAdminLevel === null && selectedSiteSmplPntId && selectedSiteSmplPntId > 0) {
        const selectedTrendData = newTrendData
          .flat()
          .find(dataLocal => dataLocal.samplingPoint.id === selectedSiteSmplPntId);
        setTrendData([[selectedTrendData]] as OverviewDatum[][]);
      } else {
        setTrendData(newTrendData);
      }
    } else {
      setTrendData([]);
    }
  }, [trendDataByLevel, shownAdminLevel, selectedSiteSmplPntId]);

  // 4.2 SELECT MAP DATA BASED ON SHOWN LEVEL
  useEffect(() => {
    if (!mapDataByLevel) {
      setMapData([]);
      return;
    }
    const adminLevelKey = getOverviewLevelKey(shownAdminLevel);
    const mapDataForLevel = mapDataByLevel[adminLevelKey] ?? [];
    const newMapData = mapDataForLevel.filter(d => d.samplingPoint.country === selectedCountry || !selectedCountry);
    setMapData(newMapData);
  }, [mapDataByLevel, selectedCountry, shownAdminLevel]);

  // 4.3 COMPUTE KEY INSIGHTS FOR REDUCTION SITES
  useEffect(() => {
    const siteTrendDataSeries: OverviewLineData | undefined = trendDataByLevel?.['null'];

    if (metricMode !== MetricMode.REDUCTION || !siteTrendDataSeries || !siteTrendDataSeries.length || !assaysLoaded) {
      setKeyInsightsBySite(undefined);
      return;
    }
    const insightsBySmplPntId = buildKeyInsightsBySite(siteTrendDataSeries, defaultProcessMode, getGroup, metricMode);

    setKeyInsightsBySite(insightsBySmplPntId);
  }, [trendDataByLevel, metricMode, defaultProcessMode, getGroup, assaysLoaded]);

  // 5. DETERMINE SHOWN ADMIN LEVEL WHEN
  // - MAP DATA CHANGES
  // - COUNTRY CHANGES, OR
  // -SITE VIEW GETS ENABLED/DISABLED
  const prevLevelsWithZoomableAreasRef = useRef<PossibleZoomableLevel[] | undefined>(undefined);
  const levelsWithZoomableAreas: PossibleZoomableLevel[] | undefined = useMemo(() => {
    if (processGroupLevelEnabled) {
      return undefined;
    }
    return data?.levelsWithZoomableAreas && Object.keys(data.levelsWithZoomableAreas).length > 1
      ? [1, 2]
      : selectedCountry && data?.levelsWithZoomableAreas
        ? data.levelsWithZoomableAreas[selectedCountry]
        : undefined;
  }, [processGroupLevelEnabled, data, selectedCountry]);

  const isSiteSelected = !!selectedSiteDatumForContext;
  const previousAdminAreasLifoRef = useRef<AdminArea[]>([]);

  previousAdminAreasLifoRef.current = previousAdminAreasLifo;

  useEffect(() => {
    if (!mapDataByLevel) {
      return;
    }

    // Update shownAdminLevel when country has changed
    let nextShownAdminLevel = shownAdminLevelRef.current;
    const leafLevel: OverviewShownLevel = null;
    const previousLifo = previousAdminAreasLifoRef.current;
    // LevelsWithZoomableAreas changes when selectedCountry changes or when mapDataByLevel is set or changed
    if (levelsWithZoomableAreas !== prevLevelsWithZoomableAreasRef.current) {
      prevLevelsWithZoomableAreasRef.current = levelsWithZoomableAreas;
      // There are no zoomable areas to show, so we show the markers on the map
      if (!levelsWithZoomableAreas?.length) {
        nextShownAdminLevel = leafLevel;
        // The country was selected, prepare the new admin levels
      } else if (levelsWithZoomableAreas[0] === 2 && previousLifo.length === 1) {
        nextShownAdminLevel = levelsWithZoomableAreas.find(level => level > 2) || leafLevel;
        const nextArea =
          typeof nextShownAdminLevel === 'number'
            ? mapDataByLevel[nextShownAdminLevel][0]?.samplingPoint.adminLevels?.[
                `${nextShownAdminLevel}` as AdminLevelKey
              ] || null
          : null;
        setZoomedAdminAreaStable(nextArea);
      }
    }

    if (isSiteSelected) {
      // Set shownAdminLevel to null for site is selected
      nextShownAdminLevel = null;
    } else if (keys(mapDataByLevel).length && levelsWithZoomableAreas?.length && zoomedAdminAreaRef.current === null) {
      // Set shownAdminLevel upon landing on non-site overview with data
      const highestLevel = levelsWithZoomableAreas[0];
      const levelMapData = mapDataByLevel[highestLevel];
      const firstArea = levelMapData[0]?.samplingPoint.adminLevels?.[`${highestLevel}` as AdminLevelKey] || null;
      nextShownAdminLevel = levelsWithZoomableAreas.find(level => firstArea && level > firstArea.level) || leafLevel;
      setZoomedAdminAreaStable(firstArea);
    }

    if (nextShownAdminLevel !== shownAdminLevelRef.current) {
      setShownAdminLevelStable(nextShownAdminLevel);
    }
  }, [mapDataByLevel, levelsWithZoomableAreas, isSiteSelected, processGroupLevelEnabled]);

  const siteTrendData = trendDataByLevel?.['null'];
  const analyzedAntibioticGroups = useMemo(
    () => getAnalysedGroupsAndAll(siteTrendData, getGroup),
    [siteTrendData, getGroup],
  );

  const effectiveSiteProcessMode = selectedSiteDatumForContext
    ? getEffectiveSiteProcessMode(selectedSiteDatumForContext, defaultProcessMode)
    : defaultProcessMode;
  const { selectedProcessId, selectedStepPointId, selectedFlowId, comparedStepPointId } = processSelection;

  // CONTEXT DATA ASSEMBLY
  const contextData: OverviewContextData = {
    hasPreparedData: !!trendData && !!mapData,
    mapData,
    mapDataByLevel,
    levelsWithZoomableAreas,
    selectedSiteDatum: selectedSiteDatumForContext,
    trendData,
    selectedAntibiotic,
    previousAdminAreasLifo,
    loading: loading || !queryParamsInitialised || !assaysLoaded,
    error,
    setHoveredAreaOrSiteSmplPntIdStable,
    selectedOrHoveredAreaOrSiteSmplPntId: hoveredAreaOrSiteSmplPntId || selectedSiteSmplPntId,
    hoveredAreaOrSiteSmplPntId,
    defaultProcessMode,
    effectiveSiteProcessMode,
    shownAdminLevel,
    processGroupLevelEnabled,
    selectedProcessId,
    selectedStepPointId,
    selectedFlowId,
    comparedStepPointId,
    setProcessSelectionStable,
    isOneHealthProject,
    metricGeneCnt,
    availableSmplPntGroups,
    zoomedAdminAreaRef,
    zoomedMapData,
    metricMode,
    notAvailableReason,
    availableMonths,
    setMonthStable: queryFilters.setMonthStable,
    // Currently the interval.start can present the 1900 year, which is less than 0
    selectedMonth: queryFilters.filters.interval.start.valueOf() > 0 ? queryFilters.filters.interval.start : null,
    selectedSampleTypeGroup: queryFilters.filters.selectedSampleTypeGroup,
    activeOverviewConfiguration,
    activeAbundanceMode,
    selectedCountry,
    supportedSamples,
    trendDataByLevel,
    processGroupMapData,
    setPreviousAdminAreasLifoStable,
    setZoomedAdminAreaStable,
    setSelectedCountryStable,
    setShownAdminLevelStable,
    analyzedAntibioticGroups,
    referenceLineData,
    keyInsightsBySite,
  };

  return <OverviewContext.Provider value={contextData}>{children}</OverviewContext.Provider>;
}

function doesDatumContainSamplingPoint(datum: OverviewDatum, samplingPointId: number): boolean {
  const originalSamplingPointId = getOriginalSamplingPointId(datum.samplingPoint);
  if (originalSamplingPointId === samplingPointId) {
    return true;
  }
  const poolingKey = (datum.samplingPoint as { poolingKey?: string }).poolingKey;
  return !!poolingKey?.startsWith('process-') && !!datum.processStepPointIds?.includes(samplingPointId);
}

function getPrimaryStepAbundances(datum: OverviewDatum): OverviewDatum['beforeAbundances'] {
  return datum.beforeAbundances?.length ? datum.beforeAbundances : datum.afterAbundances;
}

function logProcessSelectionFallback(
  payload: {
    fallbackKeysRef: React.MutableRefObject<Set<string>>;
    reason: string;
  } & Record<string, unknown>,
) {
  const { fallbackKeysRef, reason, ...details } = payload;
  const fallbackKey = [
    reason,
    getFallbackKeyValue(details.selectedProcessId),
    getFallbackKeyValue(details.selectedStepPointId),
    getFallbackKeyValue(details.selectedFlowId),
    getFallbackKeyValue(details.fallbackProcessId),
    getFallbackKeyValue(details.fallbackStepPointId),
    getFallbackKeyValue(details.fallbackFlowId),
  ].join(':');
  if (fallbackKeysRef.current.has(fallbackKey)) {
    return;
  }
  fallbackKeysRef.current.add(fallbackKey);
  console.warn('[overview-process-selection-fallback]', { reason, ...details });
}

function getFallbackKeyValue(value: unknown): string {
  if (value === null || value === undefined) {
    return 'none';
  }
  if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
    return `${value}`;
  }
  return 'object';
}

export function useOverviewContext() {
  const context = useContext(OverviewContext);
  if (!context) {
    throw new Error('useOverviewContext must be used within a OverviewContextProvider');
  }
  return context;
}
